package app.smartcoding.realrate.presentation.components

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.dp
import app.smartcoding.realrate.domain.model.AmortizationRow
import app.smartcoding.realrate.domain.model.CreditRating
import app.smartcoding.realrate.localization.LocalStrings
import io.github.koalaplot.core.ChartLayout
import io.github.koalaplot.core.animation.StartAnimationUseCase
import io.github.koalaplot.core.bar.DefaultBarPosition
import io.github.koalaplot.core.bar.DefaultVerticalBarPlotEntry
import io.github.koalaplot.core.bar.VerticalBarPlot
import io.github.koalaplot.core.bar.verticalSolidBar
import io.github.koalaplot.core.style.KoalaPlotTheme
import io.github.koalaplot.core.util.ExperimentalKoalaPlotApi
import io.github.koalaplot.core.xygraph.AxisContent
import io.github.koalaplot.core.xygraph.AxisStyle
import io.github.koalaplot.core.xygraph.TickPosition
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberIntLinearAxisModel
import kotlinx.collections.immutable.ImmutableList

@OptIn(ExperimentalKoalaPlotApi::class)
@Composable
fun Result3(schedule: ImmutableList<AmortizationRow>) {
    Card(
        modifier = Modifier.fillMaxWidth().height(248.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceContainerLow),
    ) {
        if (schedule.isEmpty()) return@Card

        val chartAnimationSpecs = KoalaPlotTheme.animationSpec

        val startAnimationUseCase = remember(schedule) {
            StartAnimationUseCase(
                executionType = StartAnimationUseCase.ExecutionType.None,
                chartAnimationSpecs,
            )
        }
        Column(modifier = Modifier.padding(16.dp)) {
            Text(
                text = LocalStrings.current.paymentComposition,
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.padding(bottom = 16.dp)
            )

            val maxPayment = remember(schedule) {
                schedule.maxOfOrNull { it.payment.toInt() } ?: 100
            }

            val principalData by remember(schedule) {
                derivedStateOf {
                    schedule.map {
                        DefaultVerticalBarPlotEntry(
                            x = it.month,
                            y = DefaultBarPosition(0, it.principal.toInt())
                        )
                    }
                }
            }

            val interestData by remember(schedule) {
                derivedStateOf {
                    schedule.map {
                        DefaultVerticalBarPlotEntry(
                            x = it.month,
                            y = DefaultBarPosition(
                                it.principal.toInt(),
                                (it.principal + it.interest).toInt()
                            )
                        )
                    }
                }
            }

            ChartLayout(
                modifier = Modifier
                    .fillMaxWidth()
                    .graphicsLayer(),
            ) {
                val xAxisModel = rememberIntLinearAxisModel(
                    range = 0..(schedule.size * 1.02).toInt(),
                    minorTickCount = 0
                )

                val yAxisModel = rememberIntLinearAxisModel(
                    range = 0..maxPayment,
                    minorTickCount = 0,
                    minimumMajorTickSpacing = 20.dp,
                )

                XYGraph(
                    xAxisModel = xAxisModel,
                    yAxisModel = yAxisModel,
                    xAxisContent = AxisContent(
                        labels = {
                            Text("$it", style = MaterialTheme.typography.labelSmall)
                        },
                        title = {},
                        style = AxisStyle(
                            majorTickSize = 4.dp,
                            tickPosition = TickPosition.Outside,
                        )
                    ),
                    yAxisContent =  AxisContent(
                        labels = {
                            Text("$it", style = MaterialTheme.typography.labelSmall)
                        },
                        title = {},
                        style = AxisStyle(
                            majorTickSize = 4.dp,
                            tickPosition = TickPosition.Outside,
                        )
                    ),
                ) {
                    VerticalBarPlot(
                        barWidth = 1f,
                        data = principalData,
                        bar = verticalSolidBar(CreditRating.EXCELLENT.color),
                        startAnimationUseCase = startAnimationUseCase
                    )

                    VerticalBarPlot(
                        barWidth = 1f,
                        data = interestData,
                        bar = verticalSolidBar(CreditRating.BAD.color),
                        startAnimationUseCase = startAnimationUseCase
                    )
                }
            }
        }
    }
}
